import { type ComponentProps } from 'react'
import { F0Box, F0Icon, F0Text } from '@factorialco/f0-react'
import { F0Callout } from '@factorialco/f0-react/dist/experimental'
import {
  AlertCircle,
  CheckCircle,
  ClockBack,
  DottedCircle,
  PartiallyCompleted,
} from '@factorialco/f0-react/icons/app'

import type { IntegrationsAgreggatedSyncableStateValuesEnum } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import {
  type AggregatedStatus,
  useTranslatedAggregatedStatus,
  useIntegrationsSyncContext,
  useSyncableStateStatistics,
} from 'modules/integrations/public'

type IconColor = 'warning' | 'critical' | 'positive' | 'default'
type CalloutVariant = 'positive' | 'critical' | 'info' | 'warning'

type StatusItem = {
  key: AggregatedStatus
  count: number
  icon: ComponentProps<typeof F0Icon>['icon']
  iconColor: IconColor
}

const TRANSITION_DURATION = 400

const SyncStatusStats = () => {
  const { isSyncRunning } = useIntegrationsSyncContext()
  const { statistics, loading } = useSyncableStateStatistics({ skip: isSyncRunning })
  const translateStatus = useTranslatedAggregatedStatus()

  const countFor = (status: IntegrationsAgreggatedSyncableStateValuesEnum): number =>
    statistics?.statusCounts.find((sc) => sc.status === status)?.count ?? 0

  const pendingCount = countFor('pending')
  const syncedCount = countFor('synced')
  const outdatedCount = countFor('outdated')
  const partiallySyncedCount = countFor('partially_synced')
  const failedCount = countFor('failed')
  const totalCount = statistics?.totalCount ?? 0

  const items: StatusItem[] = [
    {
      key: 'pending',
      count: pendingCount,
      icon: DottedCircle,
      iconColor: 'default',
    },
    {
      key: 'outdated',
      count: outdatedCount,
      icon: ClockBack,
      iconColor: 'warning',
    },
    {
      key: 'partiallySynced',
      count: partiallySyncedCount,
      icon: PartiallyCompleted,
      iconColor: 'warning',
    },
    {
      key: 'failed',
      count: failedCount,
      icon: AlertCircle,
      iconColor: 'critical',
    },
    {
      key: 'synced',
      count: syncedCount,
      icon: CheckCircle,
      iconColor: 'positive',
    },
  ]

  const visibleItems = items.filter((item) => item.count > 0)
  const isLoading = !isSyncRunning && loading
  const shouldShow = !isSyncRunning && !loading && !!statistics
  const hasContent = shouldShow && visibleItems.length > 0
  const isExpanded = isLoading || hasContent

  const resolveCalloutConfig = (): { variant: CalloutVariant; title: string } => {
    // Up-to-date: 100% synced
    if (syncedCount === totalCount) {
      return {
        variant: 'positive',
        title: i18n.t('bookkeepers.clients.payroll_cycles.sync_status_stats.titles.all_synced'),
      }
    }

    // First time: 100% pending
    if (pendingCount === totalCount) {
      return {
        variant: 'info',
        title: i18n.t('bookkeepers.clients.payroll_cycles.sync_status_stats.titles.all_pending'),
      }
    }

    // Failed: at least 1 failed
    if (failedCount > 0) {
      return {
        variant: 'critical',
        title: i18n.t('bookkeepers.clients.payroll_cycles.sync_status_stats.titles.failed'),
      }
    }

    // Partial sync: at least 1 partially synced
    if (partiallySyncedCount > 0) {
      return {
        variant: 'warning',
        title: i18n.t(
          'bookkeepers.clients.payroll_cycles.sync_status_stats.titles.partially_synced'
        ),
      }
    }

    // New changes: at least 1 pending or outdated
    return {
      variant: 'info',
      title: i18n.t('bookkeepers.clients.payroll_cycles.sync_status_stats.titles.pending'),
    }
  }

  const { variant, title } = resolveCalloutConfig()

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateRows: isExpanded ? '1fr' : '0fr',
        transition: `grid-template-rows ${TRANSITION_DURATION}ms ease`,
      }}
    >
      <F0Box overflow="hidden">
        <div
          style={{
            opacity: isExpanded ? 1 : 0,
            transform: isExpanded ? 'translateY(0)' : 'translateY(-8px)',
            transition: `opacity ${TRANSITION_DURATION - 100}ms ease ${isExpanded ? '50ms' : '0ms'}, transform ${TRANSITION_DURATION - 50}ms ease ${isExpanded ? '50ms' : '0ms'}`,
          }}
        >
          <F0Box display="flex" paddingX="xl" paddingY="none" marginBottom="md">
            {isLoading ? (
              <F0Callout.Skeleton compact variant="info" />
            ) : (
              <F0Callout variant={variant} title={title}>
                <F0Box
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  gap="3xl"
                  paddingBottom="md"
                >
                  <F0Text
                    variant="description"
                    content={i18n.t(
                      'bookkeepers.clients.payroll_cycles.sync_status_stats.description'
                    )}
                  />
                </F0Box>
                <F0Box
                  display="flex"
                  flexDirection="row"
                  gap="lg"
                  flexWrap="wrap"
                  alignItems="center"
                >
                  {visibleItems.map((item) => (
                    <F0Box
                      key={item.key}
                      display="flex"
                      flexDirection="row"
                      alignItems="center"
                      gap="sm"
                    >
                      <F0Icon icon={item.icon} size="lg" color={item.iconColor} />
                      <F0Text variant="body" content={`${translateStatus(item.key)}:`} />
                      <F0Text variant="label" content={`${item.count}`} />
                    </F0Box>
                  ))}
                </F0Box>
              </F0Callout>
            )}
          </F0Box>
        </div>
      </F0Box>
    </div>
  )
}

export default SyncStatusStats
